import { useEffect } from "react";

import { useConfig } from "../../core/config";
import { useAnalytics } from "../../lib/analytics/analytics";
import { makeHotjarClient } from "../../lib/analytics/hotjar";
import { makeMixpanelClient } from "../../lib/analytics/mixpanel";
import { makeGA4Client } from "../../lib/analytics/ga4";
import { makeGTMClient } from "@/lib/analytics/gtm";
import { makeGoogleBookClient } from "@/lib/analytics/google_book";
import { useGoogleBookProductionEnvironmentFeatureFlag } from "@/core/splitio";
import { makeCustomerioClient } from "@/lib/analytics/customerio";

export function useFlexspaceAnalytics() {
  const config = useConfig();
  const analytics = useAnalytics();

  const googleBookProductionEnvironmentFeatureFlag =
    useGoogleBookProductionEnvironmentFeatureFlag();

  useEffect(() => {
    const variables = envVariables[config.environment];

    if (variables.gtmId) {
      const gtmClient = makeGTMClient({ gtmId: variables.gtmId });

      analytics.addClient(gtmClient);
    }

    // Google Analytics
    if (variables.ga4TrackingId) {
      const ga4Client = makeGA4Client({
        trackingId: variables.ga4TrackingId,
        gaOptions: {
          name: "flexspace",
        },
      });

      analytics.preloadClient(ga4Client);
    }

    if (variables.customerioWriteKey) {
      const customerioClient = makeCustomerioClient({
        writeKey: variables.customerioWriteKey,
      });

      analytics.addClient(customerioClient);
    }

    // Hotjar
    if (variables.hotjarId && variables.hotjarSnippetVersion) {
      const hotjarClient = makeHotjarClient({
        hotjarTrackingID: variables.hotjarId,
        hotjarSnippetVersion: variables.hotjarSnippetVersion,
      });

      analytics.addClient(hotjarClient);
    }

    // Mixpanel
    if (!!variables.mixpanelToken) {
      const mixpanelClient = makeMixpanelClient({
        token: variables.mixpanelToken,
        debug: variables.debug,
      });

      analytics.addClient(mixpanelClient);
    }

    // Google Book now
    // Only add Google Book Now client in production environment
    // becase the tracking data must match data on production
    if (config.environment === "production") {
      const googleBookNowClient = makeGoogleBookClient(
        googleBookProductionEnvironmentFeatureFlag ? "production" : "sandbox"
      );

      analytics.addClient(googleBookNowClient);
    }
  }, [
    analytics,
    config.environment,
    googleBookProductionEnvironmentFeatureFlag,
  ]);
}

const envVariables = {
  development: {
    gtmId: "",
    ga4TrackingId: "",
    mixpanelToken: "",
    hotjarId: null,
    hotjarSnippetVersion: null,
    debug: true,
    customerioWriteKey: "",
  },
  staging: {
    gtmId: "",
    ga4TrackingId: "G-68BJCMB8NC",
    mixpanelToken: "5f75336d2da8f13b69edb312d4056182",
    hotjarId: 2005679,
    hotjarSnippetVersion: 6,
    debug: true,
    customerioWriteKey: "",
  },
  production: {
    gtmId: "",
    ga4TrackingId: "G-BDJ288BPKN",
    mixpanelToken: "33d4dfb7ce588749a4fac972c611d8f1",
    hotjarId: 2003465,
    hotjarSnippetVersion: 6,
    debug: false,
    customerioWriteKey: "dfa0a05b93b0faa5ceb6",
  },
};
